import { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ecolabelOptions, retailerOptions, stateRanges } from '@/data/benchmarkData';

export interface BenchmarkFilters {
  search: string;
  productType: string;
  manufacturer: string;
  ecolabels: string[];
  retailers: string[];
  stateRange: string;
}

interface BenchmarkFiltersProps {
  filters: BenchmarkFilters;
  onFilterChange: (filters: Partial<BenchmarkFilters>) => void;
  onClearFilters: () => void;
}

export const BenchmarkFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters 
}: BenchmarkFiltersProps) => {
  const [localSearch, setLocalSearch] = useState(filters.search);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange({ search: localSearch });
  };

  const handleEcolabelChange = (ecolabel: string, checked: boolean) => {
    const newEcolabels = checked 
      ? [...filters.ecolabels, ecolabel]
      : filters.ecolabels.filter(e => e !== ecolabel);
    onFilterChange({ ecolabels: newEcolabels });
  };

  const handleRetailerChange = (retailer: string, checked: boolean) => {
    const newRetailers = checked 
      ? [...filters.retailers, retailer]
      : filters.retailers.filter(r => r !== retailer);
    onFilterChange({ retailers: newRetailers });
  };

  const hasActiveFilters = filters.search || filters.productType || filters.manufacturer || 
                          filters.ecolabels.length > 0 || filters.retailers.length > 0 || filters.stateRange;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Benchmark Filters
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="ml-auto h-auto p-1"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="space-y-2">
          <label className="text-sm font-medium">Search Products</label>
          <div className="flex gap-2">
            <Input
              placeholder="Search by name or manufacturer..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
            <Button type="submit" size="sm">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </form>

        {/* Product Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Product Type</label>
          <Select
            value={filters.productType}
            onValueChange={(value) => onFilterChange({ productType: value === 'all' ? '' : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="cleaning_product">Cleaning Product</SelectItem>
              <SelectItem value="paper_product">Paper Product</SelectItem>
              <SelectItem value="laundry_product">Laundry Product</SelectItem>
              <SelectItem value="formulated">Formulated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Ecolabels */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Ecolabels</label>
          <div className="space-y-2">
            {ecolabelOptions.map((ecolabel) => (
              <div key={ecolabel} className="flex items-center space-x-2">
                <Checkbox
                  id={`ecolabel-${ecolabel}`}
                  checked={filters.ecolabels.includes(ecolabel)}
                  onCheckedChange={(checked) => handleEcolabelChange(ecolabel, !!checked)}
                />
                <label
                  htmlFor={`ecolabel-${ecolabel}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {ecolabel}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Retailers */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Retailers</label>
          <div className="space-y-2">
            {retailerOptions.map((retailer) => (
              <div key={retailer} className="flex items-center space-x-2">
                <Checkbox
                  id={`retailer-${retailer}`}
                  checked={filters.retailers.includes(retailer)}
                  onCheckedChange={(checked) => handleRetailerChange(retailer, !!checked)}
                />
                <label
                  htmlFor={`retailer-${retailer}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {retailer}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* State Range */}
        <div className="space-y-2">
          <label className="text-sm font-medium">State Coverage</label>
          <Select
            value={filters.stateRange}
            onValueChange={(value) => onFilterChange({ stateRange: value === 'all' ? '' : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Any coverage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any coverage</SelectItem>
              {stateRanges.map((range) => (
                <SelectItem key={range.label} value={range.label}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Manufacturer */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Manufacturer</label>
          <Input
            placeholder="Enter manufacturer name..."
            value={filters.manufacturer}
            onChange={(e) => onFilterChange({ manufacturer: e.target.value })}
          />
        </div>
      </CardContent>
    </Card>
  );
};